import styled from "@emotion/styled";
import React from "react";

const StyledParagraph = styled.div`
  label: StyledParagraph;
  margin: 0.5rem;
  line-height: 1.6;
`;

const StyledHeading = styled.h1`
  label: StyledHeading;
  margin: 2rem 0 1rem;
  font-weight: bold;
`;

const StyledList = styled.ul`
  label: StyledList;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const StyledAlignedDiv = styled.div`
  label: StyledAlignedDiv;
  text-align: ${(props) => props.align || "left"};
`;

// Add this helper function before the RichText component
export const isEmpty = (value) => {
  if (!value) return true;
  try {
    const isEmptyNode = (node) => {
      if (node.text !== undefined) {
        return !node.text || node.text.trim() === "";
      }

      return node.children?.every(isEmptyNode) ?? true;
    };

    const parsed = JSON.parse(value?.replaceAll("\n", "") || "{}");
    if (Array.isArray(parsed)) {
      return parsed.every((block) => isEmptyNode(block));
    }
    return isEmptyNode(parsed);
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const RichText = ({ value }) => {
  if (!value || value === "" || isEmpty(value)) return null;

  const renderElement = (element, index) => {
    // Helper function to wrap content with alignment if needed
    const withAlignment = (content) => {
      return element.textAlign ? (
        <StyledAlignedDiv align={element.textAlign}>{content}</StyledAlignedDiv>
      ) : (
        content
      );
    };

    // console.info("🔍 element", element.type, element);

    switch (element.type) {
      case "upload":
        return withAlignment(
          <img
            key={index}
            src={element.value?.url}
            alt={element.value?.filename}
            width={element.value?.width}
            height={element.value?.height}
          />,
        );

      case "paragraph":
        return withAlignment(
          <StyledParagraph key={index}>
            {element.children.map((child, i) =>
              renderLeaf(child, i, `p-${index}-${i}`),
            )}
          </StyledParagraph>,
        );

      case "heading-one":
        return withAlignment(
          <StyledHeading as="h1" key={index}>
            {element.children.map((child, i) =>
              renderLeaf(child, i, `h1-${index}-${i}`),
            )}
          </StyledHeading>,
        );

      case "heading-two":
        return withAlignment(
          <StyledHeading as="h2" key={index}>
            {element.children.map((child, i) =>
              renderLeaf(child, i, `h2-${index}-${i}`),
            )}
          </StyledHeading>,
        );

      case "bulleted-list":
      case "ul":
        return (
          <StyledList key={index}>
            {element.children.map((child, i) => renderElement(child, i))}
          </StyledList>
        );

      case "list-item":
      case "li":
        return (
          <li key={index}>
            {element.children.map((child, i) =>
              renderLeaf(child, i, `li-${index}-${i}`),
            )}
          </li>
        );

      case "link":
        return withAlignment(
          <a
            key={index}
            href={element.url}
            target={element.newTab ? "_blank" : undefined}
            rel={element.newTab ? "noopener noreferrer" : undefined}
          >
            {element.children.map((child, i) =>
              renderLeaf(child, i, `link-${index}-${i}`),
            )}
          </a>,
        );

      default:
        return withAlignment(
          <StyledParagraph key={index}>
            {element.children.map((child, i) =>
              renderLeaf(child, i, `default-${index}-${i}`),
            )}
          </StyledParagraph>,
        );
    }
  };

  const renderLeaf = (leaf, index, keyPrefix) => {
    if (leaf.type) {
      return renderElement(leaf, index);
    }

    if (leaf.children) {
      return leaf.children
        ?.map((child, i) => renderLeaf(child, i, `${keyPrefix}-${i}`))
        .join("");
    }

    let text = leaf.text ?? "";

    if (leaf.bold) {
      text = <strong key={`${keyPrefix}-bold`}>{text}</strong>;
    }
    if (leaf.italic) {
      text = <em key={`${keyPrefix}-italic`}>{text}</em>;
    }
    if (leaf.underline) {
      text = <u key={`${keyPrefix}-underline`}>{text}</u>;
    }
    if (leaf.code) {
      text = <code key={`${keyPrefix}-code`}>{text}</code>;
    }

    if (leaf.color) {
      text = (
        <span key={`${keyPrefix}-color`} style={{ color: leaf.color }}>
          {text}
        </span>
      );
    }

    return text;
  };

  try {
    const parsedValue = JSON.parse(value?.replaceAll("\n", "") || "{}");
    const content = parsedValue.map((element, i) => renderElement(element, i));

    return <>{content}</>;
  } catch (e) {
    console.error(e);
    return value;
  }
};

export const toPlainText = (value) => {
  try {
    const extractText = (node) => {
      if (node.text !== undefined) {
        return node.text;
      }

      return node.children?.map(extractText).join("") ?? "";
    };

    const parsed = JSON.parse(value?.replaceAll("\n", "") || "{}");
    if (Array.isArray(parsed)) {
      return parsed.map(extractText).join("");
    }
    return extractText(parsed);
  } catch (e) {
    console.error(e);
    return "";
  }
};
